const STORAGE_KEY = 'gang_wish_list';
const CHANGE_EVENT = 'wishlist:change';

class WishListStore {
  static getHandles() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((h) => typeof h === 'string' && h.length > 0) : [];
    } catch {
      return [];
    }
  }

  static save(handles) {
    const unique = [...new Set(handles)];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(unique));
    WishListCount.sync();
    document.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, {
        detail: { handles: unique, count: unique.length },
      })
    );
  }

  static has(handle) {
    return WishListStore.getHandles().includes(handle);
  }

  static toggle(handle) {
    const handles = WishListStore.getHandles();
    const index = handles.indexOf(handle);
    if (index >= 0) {
      handles.splice(index, 1);
    } else {
      handles.unshift(handle);
    }
    WishListStore.save(handles);
    return index < 0;
  }

  static remove(handle) {
    WishListStore.save(WishListStore.getHandles().filter((h) => h !== handle));
  }
}

class WishListCount {
  static sync() {
    const count = WishListStore.getHandles().length;
    const label = count > 99 ? '99+' : count > 0 ? String(count) : '';

    document.querySelectorAll('[data-wish-list-count]').forEach((badge) => {
      badge.classList.toggle('is-visible', count > 0);
      badge.textContent = label;
    });

    document.querySelectorAll('[data-wish-list-anchor]').forEach((anchor) => {
      const text = count > 0 ? `Wish list, ${count}` : 'Wish list';
      anchor.setAttribute('aria-label', text);
      anchor.setAttribute('title', text);
    });

    document.querySelectorAll('[data-wish-list-sr-only]').forEach((el) => {
      el.textContent = count > 0 ? `Wish list, ${count}` : 'Wish list';
    });
  }
}

class WishListToggle extends HTMLElement {
  connectedCallback() {
    this.#button = this.querySelector('[data-wish-list-toggle]');
    this.#handle = this.dataset.productHandle;

    if (!this.#button || !this.#handle) return;

    this.#button.addEventListener('click', this.#onClick);
    document.addEventListener(CHANGE_EVENT, this.#sync);
    this.#sync();
  }

  disconnectedCallback() {
    this.#button?.removeEventListener('click', this.#onClick);
    document.removeEventListener(CHANGE_EVENT, this.#sync);
  }

  #button = null;
  #handle = '';

  #onClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    WishListStore.toggle(this.#handle);
    this.#sync();
  };

  #sync = () => {
    const active = WishListStore.has(this.#handle);
    this.classList.toggle('is-active', active);
    this.#button?.setAttribute('aria-pressed', String(active));

    const label = this.#button?.querySelector('[data-wish-list-label]');
    if (label) {
      const strings = window.themeVariables?.strings?.wishList || {};
      label.textContent = active
        ? strings.remove || 'Αφαίρεση από τη Wish list'
        : strings.add || 'Προσθήκη στη Wish list';
    }
  };
}

class WishListPage extends HTMLElement {
  connectedCallback() {
    this.#grid = this.querySelector('[data-wish-list-grid]');
    this.#empty = this.querySelector('[data-wish-list-empty]');
    this.#modals = this.querySelector('[data-wish-list-modals]');
    document.addEventListener(CHANGE_EVENT, this.#render);
    this.addEventListener('change', this.#onModalOptionChange);
    this.addEventListener('click', this.#onModalAdd);
    this.#render();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.#render);
    this.removeEventListener('change', this.#onModalOptionChange);
    this.removeEventListener('click', this.#onModalAdd);
  }

  #grid = null;
  #empty = null;
  #modals = null;

  #render = async () => {
    const handles = WishListStore.getHandles();

    if (!handles.length) {
      this.#grid?.replaceChildren();
      this.#modals?.replaceChildren();
      this.#empty?.removeAttribute('hidden');
      return;
    }

    this.#empty?.setAttribute('hidden', '');

    const products = (
      await Promise.all(
        handles.map(async (handle) => {
          try {
            const response = await fetch(`${Shopify.routes.root}products/${handle}.js`);
            if (!response.ok) return null;
            return response.json();
          } catch {
            return null;
          }
        })
      )
    ).filter(Boolean);

    if (!this.#grid) return;

    const strings = window.themeVariables?.strings?.wishList || {};
    const moneyFormat = window.themeVariables?.settings?.moneyFormat || '{{amount}} €';

    this.#grid.innerHTML = products
      .map((product) => this.#itemHtml(product, strings, moneyFormat))
      .join('');

    if (this.#modals) {
      this.#modals.innerHTML = products
        .filter((product) => product.variants.length > 1)
        .map((product) => this.#modalHtml(product, strings, moneyFormat))
        .join('');
    }

    this.#bindItemActions();
  };

  #bindItemActions() {
    this.#grid?.querySelectorAll('[data-wish-list-remove]').forEach((button) => {
      button.addEventListener('click', () => {
        WishListStore.remove(button.dataset.wishListRemove);
      });
    });

    this.#grid?.querySelectorAll('[data-wish-list-add]').forEach((button) => {
      button.addEventListener('click', async () => {
        const variantId = Number(button.dataset.wishListAdd);
        if (!variantId || button.disabled) return;
        button.disabled = true;
        try {
          await this.#addToCart(variantId);
        } finally {
          button.disabled = false;
        }
      });
    });
  }

  #onModalOptionChange = (event) => {
    if (!event.target.matches('[data-wish-list-option]')) return;
    const form = event.target.closest('[data-wish-list-form]');
    if (form) this.#syncModalVariant(form);
  };

  #onModalAdd = async (event) => {
    const button = event.target.closest('[data-action="wish-list-modal-add"]');
    if (!button) return;
    event.preventDefault();
    const variantId = Number(button.getAttribute('data-variant-id'));
    const form = button.closest('[data-wish-list-form]');
    const errorNode = form?.querySelector('[data-wish-list-error]');
    if (!variantId || button.disabled) return;
    button.disabled = true;
    try {
      await this.#addToCart(variantId);
    } catch (error) {
      if (errorNode) {
        errorNode.hidden = false;
        errorNode.textContent = error.message;
      }
    } finally {
      button.disabled = false;
    }
  };

  async #addToCart(variantId) {
    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
    });

    if (!response.ok) {
      let message = 'Add to cart failed';
      try {
        const content = await response.json();
        message = content.description || content.message || message;
      } catch {
        /* ignore */
      }
      throw new Error(message);
    }

    try {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch {
      /* third-party cart listeners */
    }

    try {
      document.querySelector('.Modal[aria-hidden="false"] [data-action="close-modal"]')?.click();
      document.querySelector('[data-action="open-drawer"][data-drawer-id="sidebar-cart"]')?.click();
    } catch {
      /* third-party click listeners */
    }
  }

  #formatMoney(cents, format) {
    const value = cents / 100;
    const amount = value.toFixed(2);
    const amountNoDecimals = Math.round(value).toString();
    const amountComma = amount.replace('.', ',');
    const amountNoDecimalsComma = Math.round(value).toString();

    return format.replace(
      /\{\{\s*([\w_]+)\s*\}\}/gi,
      (_, token) => {
        switch (token.toLowerCase()) {
          case 'amount':
            return amount;
          case 'amount_no_decimals':
            return amountNoDecimals;
          case 'amount_with_comma_separator':
            return amountComma;
          case 'amount_no_decimals_with_comma_separator':
            return amountNoDecimalsComma;
          case 'amount_with_apostrophe_separator':
            return amountComma.replace(/,/g, "'");
          default:
            return '';
        }
      }
    );
  }

  #priceHtml(price, compareAt, moneyFormat) {
    if (price == null) return '';
    if (compareAt && compareAt > price) {
      return `<span class="Price Price--highlight">${this.#formatMoney(price, moneyFormat)}</span><span class="Price Price--compareAt">${this.#formatMoney(compareAt, moneyFormat)}</span>`;
    }
    return `<span class="Price">${this.#formatMoney(price, moneyFormat)}</span>`;
  }

  #escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  #isColorOption(name) {
    const labels = ['color', 'colour', 'couleur', 'colore', 'farbe', 'färg', 'farve', 'χρώμα', 'χρωμα'];
    return labels.includes(String(name || '').toLowerCase());
  }

  #optionValues(product, position) {
    const key = `option${position}`;
    return [...new Set(product.variants.map((variant) => variant[key]).filter(Boolean))];
  }

  #isOptionValueAvailable(product, position, value) {
    const key = `option${position}`;
    return product.variants.some((variant) => variant[key] === value && variant.available);
  }

  #itemHtml(product, strings, moneyFormat) {
    const variant = product.variants.find((v) => v.available) || product.variants[0];
    const image = product.featured_image || product.images?.[0];
    const imageSrc = typeof image === 'string' ? image : image?.src;
    const hasMultipleVariants = product.variants.length > 1;
    const soldOut = !product.variants.some((v) => v.available);
    const title = this.#escapeHtml(product.title);
    const productUrl = this.#escapeHtml(product.url || `${Shopify.routes.root}products/${product.handle}`);
    const modalId = `wish-list-modal-${product.id}`;
    const compareAt = product.compare_at_price_min || product.compare_at_price;
    const price = product.price_min ?? variant?.price;

    const atcLabel = soldOut
      ? strings.soldOut || 'Εξαντλήθηκε'
      : hasMultipleVariants
        ? strings.selectOptions || 'Επιλογή μεγέθους'
        : strings.addToCart || 'Προσθήκη στο καλάθι';

    const removeLabel = strings.removeItem || 'Αφαίρεση';
    const trashIcon = `<svg class="Icon Icon--trash" role="presentation" viewBox="0 0 18 18" aria-hidden="true"><path d="M3.5 5.5h11" stroke="currentColor" fill="none" stroke-width="1.2" stroke-linecap="round"></path><path d="M7 5.5V4.25a.75.75 0 0 1 .75-.75h2.5a.75.75 0 0 1 .75.75V5.5" stroke="currentColor" fill="none" stroke-width="1.2"></path><path d="M5.25 5.5l.45 8.1a.75.75 0 0 0 .75.68h5.1a.75.75 0 0 0 .75-.68l.45-8.1" stroke="currentColor" fill="none" stroke-width="1.2" stroke-linejoin="round"></path><path d="M7.5 8.25v4.25M10.5 8.25v4.25" stroke="currentColor" fill="none" stroke-width="1.2" stroke-linecap="round"></path></svg>`;

    const atcHtml = soldOut
      ? `<button type="button" class="wish-list-item__btn" disabled>${atcLabel}</button>`
      : hasMultipleVariants
        ? `<button type="button" class="wish-list-item__btn" data-action="open-modal" aria-controls="${modalId}">${atcLabel}</button>`
        : `<button type="button" class="wish-list-item__btn" data-wish-list-add="${variant.id}">${atcLabel}</button>`;

    return `
      <div class="CartItemWrapper CartItemWrapper--card wish-list-item" data-handle="${product.handle}">
        <div class="CartItem CartItem--drawerCard">
          <a href="${productUrl}" class="CartItem__ImageWrapper AspectRatio">
            <div class="AspectRatio" style="--aspect-ratio: 1">
              ${imageSrc ? `<img class="CartItem__Image" src="${imageSrc}" alt="${title}" width="76" height="76" loading="lazy">` : ''}
            </div>
          </a>
          <div class="CartItem__Info">
            <h2 class="CartItem__Title Heading">
              <a href="${productUrl}">${title}</a>
            </h2>
            <div class="CartItem__Meta Heading Text--subdued">
              <div class="CartItem__PriceList">${this.#priceHtml(price, compareAt, moneyFormat)}</div>
            </div>
            <div class="CartItem__Actions">${atcHtml}</div>
          </div>
          <button
            type="button"
            class="CartItem__Remove CartItem__Remove--icon Link"
            data-wish-list-remove="${product.handle}"
            aria-label="${removeLabel}"
          >
            ${trashIcon}
          </button>
        </div>
      </div>
    `;
  }

  #modalHtml(product, strings, moneyFormat) {
    const variant = product.variants.find((v) => v.available) || product.variants[0];
    const image = variant?.featured_image || product.featured_image || product.images?.[0];
    const imageSrc = typeof image === 'string' ? image : image?.src;
    const title = this.#escapeHtml(product.title);
    const modalId = `wish-list-modal-${product.id}`;
    const addLabel = strings.addToCart || 'Προσθήκη στο καλάθι';
    const options = product.options || [];
    const selected = [variant?.option1, variant?.option2, variant?.option3];

    const optionsHtml = options
      .map((option, index) => {
        const optionName = typeof option === 'string' ? option : option?.name;
        const position = (typeof option === 'object' && option?.position) || index + 1;
        const values = (typeof option === 'object' && Array.isArray(option?.values) && option.values.length)
          ? option.values
          : this.#optionValues(product, position);
        if (!optionName) return '';
        const isColor = this.#isColorOption(optionName);
        const listClass = isColor
          ? 'CartTopPickModal__swatches ColorSwatchList HorizontalList HorizontalList--spacingTight'
          : 'CartTopPickModal__sizes HorizontalList HorizontalList--spacingTight';

        const valuesHtml = values
          .map((value, valueIndex) => {
            const available = this.#isOptionValueAvailable(product, position, value);
            const checked = selected[index] === value ? 'checked' : '';
            const disabled = available ? '' : 'disabled';
            const id = `${modalId}-option-${position}-${valueIndex}`;
            const safeValue = this.#escapeHtml(value);

            if (isColor) {
              const color = String(value).replace(/\s+/g, '').toLowerCase();
              return `
                <li class="HorizontalList__Item">
                  <input id="${id}" class="ColorSwatch__Radio" type="radio" name="${modalId}-option-${position}" value="${safeValue}" ${checked} data-option-position="${position}" data-wish-list-option>
                  <label for="${id}" class="ColorSwatch ColorSwatch--large" data-tooltip="${safeValue}" style="background-color: ${color}">
                    <span class="u-visually-hidden">${safeValue}</span>
                  </label>
                </li>`;
            }

            return `
              <li class="HorizontalList__Item" ${available ? '' : 'data-available="false"'}>
                <input id="${id}" class="SizeSwatch__Radio" type="radio" name="${modalId}-option-${position}" value="${safeValue}" ${checked} ${disabled} data-option-position="${position}" data-wish-list-option>
                <label for="${id}" class="SizeSwatch CartTopPickModal__size">${safeValue}</label>
              </li>`;
          })
          .join('');

        return `
          <div class="CartTopPickModal__option">
            <span class="CartTopPickModal__label">${this.#escapeHtml(optionName)}</span>
            <ul class="${listClass}">${valuesHtml}</ul>
          </div>`;
      })
      .join('');

    return `
      <div id="${modalId}" class="Modal Modal--cartTopPick" aria-hidden="true" role="dialog" data-scrollable>
        <button type="button" class="Modal__Close Modal__Close--outside CartTopPickModal__close" data-action="close-modal" aria-label="Close">
          <svg class="Icon Icon--close" role="presentation" viewBox="0 0 16 14"><path d="M15 0L1 14m14 0L1 0" stroke="currentColor" fill="none" fill-rule="evenodd"></path></svg>
        </button>
        <div class="CartTopPickModal" data-wish-list-form data-product-id="${product.id}">
          <script type="application/json" data-product-json>${JSON.stringify(product).replace(/</g, '\\u003c')}</script>
          <div class="CartTopPickModal__product">
            ${
              imageSrc
                ? `<div class="CartTopPickModal__media"><div class="AspectRatio" style="--aspect-ratio: 1"><img class="CartTopPickModal__image" src="${imageSrc}" alt="${title}" data-wish-list-image width="76" height="76" loading="lazy"></div></div>`
                : ''
            }
            <div class="CartTopPickModal__info">
              <h3 class="CartTopPickModal__title">${title}</h3>
              <div class="CartTopPickModal__price" data-wish-list-price>${this.#priceHtml(variant?.price, variant?.compare_at_price, moneyFormat)}</div>
            </div>
          </div>
          ${optionsHtml}
          <p class="CartTopPickModal__error" data-wish-list-error hidden></p>
          <button type="button" class="Button Button--primary Button--full CartTopPickModal__submit" data-action="wish-list-modal-add" data-variant-id="${variant?.id || ''}" ${variant?.available ? '' : 'disabled'}>${addLabel}</button>
        </div>
      </div>
    `;
  }

  #syncModalVariant(form) {
    const jsonNode = form.querySelector('[data-product-json]');
    if (!jsonNode) return;

    let product;
    try {
      product = JSON.parse(jsonNode.textContent);
    } catch {
      return;
    }

    const options = [];
    form.querySelectorAll('[data-wish-list-option]:checked').forEach((input) => {
      options[parseInt(input.getAttribute('data-option-position'), 10) - 1] = input.value;
    });

    const variant = product.variants.find((item) =>
      options.every((value, index) => item[`option${index + 1}`] === value)
    );

    const submitButton = form.querySelector('[data-action="wish-list-modal-add"]');
    const priceNode = form.querySelector('[data-wish-list-price]');
    const imageNode = form.querySelector('[data-wish-list-image]');
    const errorNode = form.querySelector('[data-wish-list-error]');
    const moneyFormat = window.themeVariables?.settings?.moneyFormat || '{{amount}} €';

    if (!submitButton || !variant) {
      submitButton?.setAttribute('disabled', 'disabled');
      submitButton?.removeAttribute('data-variant-id');
      return;
    }

    submitButton.setAttribute('data-variant-id', String(variant.id));

    if (variant.available) {
      submitButton.removeAttribute('disabled');
      if (errorNode) {
        errorNode.hidden = true;
        errorNode.textContent = '';
      }
    } else {
      submitButton.setAttribute('disabled', 'disabled');
      if (errorNode) {
        errorNode.hidden = false;
        errorNode.textContent = 'Εξαντλημένο';
      }
    }

    if (priceNode) {
      priceNode.innerHTML = this.#priceHtml(variant.price, variant.compare_at_price, moneyFormat);
    }

    const image = variant.featured_image;
    if (imageNode && image) {
      imageNode.src = typeof image === 'string' ? image : image.src;
    }
  }
}

if (!customElements.get('wish-list-toggle')) {
  customElements.define('wish-list-toggle', WishListToggle);
}

if (!customElements.get('wish-list-page')) {
  customElements.define('wish-list-page', WishListPage);
}

const syncWishListCount = () => WishListCount.sync();

syncWishListCount();
document.addEventListener(CHANGE_EVENT, syncWishListCount);
window.addEventListener('pageshow', syncWishListCount);
